import { useMemo, useState, useCallback, useEffect } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import PediatricAssessmentCreate from "../create";
import { useTranslate } from "src/locales";
import BaseMedCardList from "src/components/shared-components/medcard/BaseMedCardList";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useAppSelector } from "src/app/hooks";
import { referenceService } from "src/api/references/referenceService";
import { PediatricAssessmentItem } from "src/api/references/referenceServicesDtos";
import ByzGrdDeleteButton from "src/components/core-components/mui/buttons-grid/ByzGrdDeleteButton";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { Box } from "@mui/material";
import { PediatricAssessmentFormData } from "../model";
import PediatricAssessmentFooter from "../components/PediatricAssessmentFooter";

type PediatricAssessmentRow = {
  id: number | string;
  date?: string;
  evaulation?: string | null;
  ped_general_view?: string | null;
  ped_cardiovascular?: string | null;
  ped_respiratory?: string | null;
  ped_total_score?: string | null;
  cuser?: string | null;
  evaulation_id?: number;
  ped_general_view_id?: number;
  ped_cardiovascular_id?: number;
  ped_respiratory_id?: number;
  raw_ptime?: string | null;
};

function PediatricAssessmentList(props: Readonly<{ patientId?: number }>) {
  const { patientId: propPatientId } = props;
  const { t } = useTranslate();
  const { id: routePatientId } = useParams();
  const mainTabs = useAppSelector((state) => state.tabs.mainTabs);
  const activeTabIndex = useAppSelector((state) => state.tabs.activeTabIndex);
  const selectedPatientId = mainTabs?.[activeTabIndex]?.data?.patientId;

  const [rows, setRows] = useState<PediatricAssessmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editInitialData, setEditInitialData] = useState<Partial<PediatricAssessmentFormData> | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    type: "success" | "error";
  }>({ open: false, message: "", type: "success" });

  const patientIdNumber = useMemo(
    () => Number(propPatientId || routePatientId || selectedPatientId || 0),
    [propPatientId, routePatientId, selectedPatientId]
  );

  const loadData = useCallback(async () => {

    try {
      setLoading(true);
      const response = await referenceService.pediatricAssessment.GetPediatricAssessment({
        PtId: Math.max(patientIdNumber, 0),
      });

      const rawData = Array.isArray(response) ? response : (response as { data?: unknown })?.data ?? response;
      const parsedData =
        typeof rawData === "string" ? JSON.parse(rawData) : rawData;

      if (Array.isArray(parsedData)) {
        const formattedRows = parsedData
          .filter((item): item is PediatricAssessmentItem => !!item)
          .map((item: PediatricAssessmentItem, index: number) => {
            const dateStr = item?.ptime ?? item?.ctime ?? null;
            const formattedDate = dateStr
              ? dayjs(dateStr).format("DD.MM.YYYY HH:mm")
              : "-";

            const rowId = item?.id ?? (item as any)?.recordId;
            const hasRealId = rowId !== undefined && rowId !== null && rowId !== 0;

            return {
              id: hasRealId ? rowId : `ui_${index}`,
              dbId: hasRealId ? rowId : null,
              date: formattedDate,
              evaulation: item?.evaulation_xname ?? (item?.evaulation && item.evaulation !== 0 ? item.evaulation.toString() : null),
              ped_general_view: item?.ped_general_view_xname ?? (item?.ped_general_view && item.ped_general_view !== 0 ? item.ped_general_view.toString() : null),
              ped_cardiovascular: item?.ped_cardiovascular_xname ?? (item?.ped_cardiovascular && item.ped_cardiovascular !== 0 ? item.ped_cardiovascular.toString() : null),
              ped_respiratory: item?.ped_respiratory_xname ?? (item?.ped_respiratory && item.ped_respiratory !== 0 ? item.ped_respiratory.toString() : null),
              ped_total_score: item?.ped_total_score ?? null,
              cuser: item?.cuser ?? null,
              evaulation_id: item?.evaulation,
              ped_general_view_id: item?.ped_general_view,
              ped_cardiovascular_id: item?.ped_cardiovascular,
              ped_respiratory_id: item?.ped_respiratory,
              raw_ptime: item?.ptime ?? item?.ctime ?? null,
            };
          });
        setRows(formattedRows);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("Pediatrik Değerlendirme listesi alınırken hata oluştu:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [patientIdNumber]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteClick = useCallback((row: PediatricAssessmentRow) => {
    const id = row.id;
    if (!id && id !== 0) return;

    // If it's a UI-only ID, don't proceed to delete
    if (typeof id === 'string' && (id.includes('ui_') || id.includes('row_'))) {
      console.warn("Veritabanı ID'si olmayan bir satır silinemez.");
      return;
    }

    const numericId = Number(id);
    if (Number.isNaN(numericId)) return;
    setDeleteItemId(numericId);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteItemId(null);
  }, []);

  const handleDeleteConfirm = useCallback(async (): Promise<boolean> => {
    if (!deleteItemId) return false;
    try {
      setIsDeleting(true);
      await referenceService.pediatricAssessment.DeletePediatricAssessment(deleteItemId);
      await loadData();
      setDeleteDialogOpen(false);
      setDeleteItemId(null);
      return true;
    } catch (error) {
      console.error("Pediatrik Değerlendirme silinirken hata oluştu:", error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [deleteItemId, loadData]);

  const openEdit = useCallback((row: PediatricAssessmentRow) => {
    if (!row.id && row.id !== 0) return;
    const numericId = Number(row.id);
    if (Number.isNaN(numericId)) return;

    setEditId(numericId);
    setEditInitialData({
      id: numericId,
      evaulation: row.evaulation_id,
      ped_general_view: row.ped_general_view_id,
      ped_cardiovascular: row.ped_cardiovascular_id,
      ped_respiratory: row.ped_respiratory_id,
      ptime: row.raw_ptime ? new Date(row.raw_ptime) : null,
    });
    setEditOpen(true);
  }, []);

  const handleShowMessage = useCallback((msg: string, type: "success" | "error") => {
    setSnackbar({
      open: true,
      message: msg,
      type,
    });
  }, []);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "update",
      headerName: t("update"),
      width: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%", justifyContent: "center" }}>
          <ByzGrdEditButton onClick={() => openEdit(params.row)} />
        </Box>
      ),
    },
    {
      field: "date",
      headerName: t("pediatric-assessment.date"),
      width: 150,
    },
    {
      field: "evaulation",
      headerName: t("pediatric-assessment.evaluation"),
      flex: 1,
    },
    {
      field: "ped_general_view",
      headerName: t("pediatric-assessment.general-appearance"),
      flex: 1,
    },
    {
      field: "ped_cardiovascular",
      headerName: t("pediatric-assessment.cardiovascular"),
      flex: 1,
    },
    {
      field: "ped_respiratory",
      headerName: t("pediatric-assessment.respiratory"),
      flex: 1,
    },
    {
      field: "ped_total_score",
      headerName: t("pediatric-assessment.total-score"),
      width: 120,
    },
    {
      field: "cuser",
      headerName: t("user", "capitalize"),
      width: 120,
    },
    {
      field: "delete",
      headerName: t("delete"),
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%", justifyContent: "center" }}>
          <ByzGrdDeleteButton
            onClick={() => handleDeleteClick(params.row)}
          />
        </Box>
      ),
    },
  ], [t, openEdit, handleDeleteClick]);

  return (
    <>
      <Backdrop
        open={isDeleting}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        patientId={deleteItemId?.toString() || ""}
        title={t("pediatric-assessment.delete-title") || "Silme Onayı"}
        content={t("pediatric-assessment.delete-confirm") || "Bu kaydı silmek istediğinizden emin misiniz?"}
        onConfirm={handleDeleteConfirm}
        hideSnackbar={true}
        onSuccess={(success) => {
          if (success) {
            handleShowMessage(t("toast.content"), "success");
            setDeleteDialogOpen(false);
            setDeleteItemId(null);
          } else {
            handleShowMessage(t("toast.error-content"), "error");
          }
        }}
      />

      <BaseMedCardList
        title={t("pediatric-assessment.title")}
        columns={columns}
        rows={rows}
        loading={loading}
        CreateComponent={PediatricAssessmentCreate}
        createComponentProps={{ patientId: patientIdNumber, onSaved: loadData, onShowMessage: handleShowMessage }}
        pageSize={10}
        customToolbar={() => null}
      />

      <PediatricAssessmentFooter />

      <OpenComponentDialog
        open={editOpen}
        title={t("pediatric-assessment.edit")}
        onClose={() => setEditOpen(false)}
      >
        <PediatricAssessmentCreate
          recordId={editId || undefined}
          patientId={patientIdNumber}
          formData={editInitialData || undefined}
          onSaved={loadData}
          onCloseModal={() => setEditOpen(false)}
        />
      </OpenComponentDialog>

      <ByzMuiSnackBar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        title={snackbar.type === "success" ? t("toast.title") : t("toast.error-title")}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </>
  );
}

export default PediatricAssessmentList;
